import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query'
import { useMemo } from 'react'
import { fetchPostComments } from '../api/commentsClient'
import type { PostCommentDto, PostCommentsResponse } from '../model/comments'

const COMMENTS_PAGE_SIZE = 10

export function usePostComments(postId: string) {
  const queryKey = ['posts', postId, 'comments'] as const

  const result = useInfiniteQuery<
    PostCommentsResponse,
    Error,
    InfiniteData<PostCommentsResponse, number>,
    typeof queryKey,
    number
  >({
    queryKey,
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      fetchPostComments(postId, { page: pageParam, limit: COMMENTS_PAGE_SIZE }, signal),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.data.length, 0)
      if (loaded >= lastPage.meta.total) return undefined
      return lastPage.meta.page + 1
    },
  })

  const comments = useMemo<PostCommentDto[]>(
    () => (result.data?.pages ?? []).flatMap((p) => p.data),
    [result.data],
  )

  return {
    comments,
    total: result.data?.pages[0]?.meta.total ?? 0,
    isLoading: result.isLoading,
    isFetchingNextPage: result.isFetchingNextPage,
    hasNextPage: Boolean(result.hasNextPage),
    fetchNextPage: result.fetchNextPage,
  }
}
